import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../../config';
import Swal from 'sweetalert2';
import Select from 'react-select';
import './CreateCustomer.css';

const CreateCustomer = () => {
  const [customerData, setCustomerData] = useState(null);
  const [contactName, setContactName] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [companyNumber, setCompanyNumber] = useState('');
  const [companyEmail, setCompanyEmail] = useState('');
  const [customerType, setCustomerType] = useState({ value: 'Individual', label: 'Individual Customer' });

  const navigate = useNavigate();

  useEffect(() => {
    const fetchAdminStatus = async () => {
      const token = localStorage.getItem('token');

      if (!token) {
        navigate('/unauthorized');
        return;
      }

      const loadingAlert = Swal.fire({
        title: 'Loading ...',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      try {
        const orgEnabledResponse = await axios.get(`${API_BASE_URL}/org/org-enabled`, {
          headers: {
            Authorization: `Bearer ${token}`,
            SelectedOrg: localStorage.getItem('selectedOrg'),
          },
        });

        const { isEnabled } = orgEnabledResponse.data;

        if (!isEnabled) {
          Swal.fire({
            title: 'Organization Disabled',
            text: 'This organization has been disabled by the admin. Please contact support for further assistance.',
            icon: 'error',
            confirmButtonText: 'OK',
          }).then(() => {
            navigate('/unauthorized');
          });
          return;
        }

        const response = await fetch(`${API_BASE_URL}/customers/customer-data`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            SelectedOrg: localStorage.getItem('selectedOrg'),
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch organization info');
        }

        const data = await response.json();
        setCustomerData(data);
      } catch (error) {
        console.error('Error fetching admin status:', error);
        navigate('/unauthorized');
      } finally {
        loadingAlert.close();
      }
    };

    fetchAdminStatus();
  }, [navigate]);

  const calculateUsagePercentage = () => {
    if (customerData) {
      return Math.floor((customerData.customer_count / customerData.max_customer) * 100);
    }
    return 0;
  };

  const isCreateButtonEnabled = customerData && customerData.customer_count < customerData.max_customer;

  // Function to validate required fields based on customer type
  const validateFields = () => {
    if (customerType.value === 'Individual') {
      return contactName && contactNumber && contactEmail;
    } else if (customerType.value === 'Company') {
      return (
        contactName && contactNumber && contactEmail &&
        companyName && companyNumber && companyEmail
      );
    }
    return false;
  };

  const handleCreateCustomer = async () => {
    if (!validateFields()) {
        Swal.fire({
            title: 'Error!',
            text: 'Please fill out all required fields.',
            icon: 'error',
            confirmButtonText: 'OK',
        });
        return;
    }

    try {
        const token = localStorage.getItem('token');
        const orgId = localStorage.getItem('selectedOrg');

        const payload = {
            contactName,
            contactNumber,
            contactEmail,
            customerType: customerType.value,
            ...(customerType.value === 'Company' && {
                companyName,
                companyNumber,
                companyEmail,
                companyWebsite: companyWebsite || "",
            }),
        };

        // Check for duplicate customers
        const duplicateResponse = await axios.post(`${API_BASE_URL}/customers/check-duplicate`, payload, {
            headers: {
                Authorization: `Bearer ${token}`,
                SelectedOrg: orgId,
                'Content-Type': 'application/json',
            },
        });

        if (duplicateResponse.data.duplicates && duplicateResponse.data.duplicates.length > 0) {
          const duplicateDetails = duplicateResponse.data.duplicates
              .map(dup => {
                  // Join the matching fields into a comma-separated string, or display 'No matching fields' if none
                  const matchingFields = dup.matchingFields ? dup.matchingFields.join(', ') : 'No matching fields specified';
                  
                  // Ensure each customer ID and the matching fields appear on new lines
                  return `Customer ID: ${dup.customer_id}\nDuplicate fields: ${matchingFields}.\n`;
              })
              .join('\n');  // Ensure each duplicate customer block is separated by a newline
      
          // Use SweetAlert to show the duplicate message
          const confirmResult = await Swal.fire({
              title: 'Duplicate Found!',
              text: `A customer with similar information already exists. Details:\n\n${duplicateDetails}\n\nDo you still want to create this customer?`,
              icon: 'warning',
              showCancelButton: true,
              confirmButtonText: 'Yes, create anyway',
              cancelButtonText: 'Cancel',
          });
      
          if (!confirmResult.isConfirmed) {
              return;
          }
        }
      

        // Proceed to create the customer if no duplicates are found or user confirmed
        const createResponse = await axios.post(`${API_BASE_URL}/customers/create`, payload, {
            headers: {
                Authorization: `Bearer ${token}`,
                SelectedOrg: orgId,
                'Content-Type': 'application/json',
            },
        });

        if (createResponse.status === 201) {
            Swal.fire({
                title: 'Success!',
                text: 'Customer created successfully!',
                icon: 'success',
                confirmButtonText: 'OK',
            }).then(() => {
                navigate('/organization/customer-management');
            });
        }
    } catch (error) {
        console.error('Error creating customer:', error);

        const errorMessage = error.response?.data?.message || 'Failed to create customer.';

        Swal.fire({
            title: 'Error!',
            text: errorMessage,
            icon: 'error',
            confirmButtonText: 'OK',
        });
    }
  };  
;



  // Options for the react-select dropdown
  const customerTypeOptions = [
    { value: 'Individual', label: 'Individual Customer' },
    { value: 'Company', label: 'Company Customer' }
  ];

  return (
    <div className="organizations-container">
      <div className="organizations-header">
        <h1>Create Customer</h1>
      </div>
      {customerData != null && (
        <div className="organization-info">
          <p>Current Plan Usage: {customerData.customer_count} / {customerData.max_customer} Customer(s)</p>
          <div className="progress-bar-container">
            <div
              className="progress-bar"
              style={{ width: `${calculateUsagePercentage()}%` }}
            >
              <span className="progress-text">{calculateUsagePercentage().toFixed(0)}%</span>
            </div>
          </div>
        </div>
      )}

      <div className="customer-type-selector">
        <label>Select Customer Type:</label>
        <select
          value={customerType.value}
          onChange={(e) => setCustomerType({ value: e.target.value, label: e.target.options[e.target.selectedIndex].text })}
        >
          {customerTypeOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className="customer-container">
        <div className="input-column">
          <label>Contact Name*</label>
          <input
            type="text"
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            placeholder="Name"
            maxLength={255}
            required
          />

          <label>Contact Number*</label>
          <input
            type="number"
            value={contactNumber}
            onChange={(e) => setContactNumber(e.target.value)}
            placeholder="0400000000"
            maxLength={20}
            required
          />

          <label>Contact Email*</label>
          <input
            type="text"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            placeholder="Email"
            maxLength={255}
            required
          />
        </div>

        {/* Conditionally render the second column based on customer type */}
        {customerType.value === 'Company' && (
          <div className="input-column">
            <label>Company Name*</label>
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Company Name"
              maxLength={255}
              required={customerType.value === 'Company'}
            />

            <label>Company Number*</label>
            <input
              type="number"
              value={companyNumber}
              onChange={(e) => setCompanyNumber(e.target.value)}
              placeholder="0400000000"
              maxLength={20}
              required={customerType.value === 'Company'}
            />

            <label>Company Email*</label>
            <input
              type="text"
              value={companyEmail}
              onChange={(e) => setCompanyEmail(e.target.value)}
              placeholder="Company Email"
              maxLength={255}
              required={customerType.value === 'Company'}
            />

            <label>Company Website</label>
            <input
              type="text"
              value={companyWebsite}
              onChange={(e) => setCompanyWebsite(e.target.value)}
              placeholder="Company Website"
              maxLength={255}
            />
          </div>
        )}
      </div>

      <div className="button-container">
        <button
          className="create-org-button"
          disabled={!isCreateButtonEnabled}
          onClick={handleCreateCustomer}
        >
          Create Customer
        </button>
      </div>

      {!isCreateButtonEnabled && (
        <p style={{ color: 'red', marginTop: '10px' }}>
          * You have reached the maximum limit for customers allowed under your plan. Please upgrade your plan or delete an existing user before creating a new one.
        </p>
      )}
    </div>
  );
};

export default CreateCustomer;
